import { cn } from "@heroui/theme";
import { BudgetBreakdownItem, CategoryConfig } from "@/types/booking";
import { X } from "lucide-react";
import { Check, Minus } from "lucide-react";

const StatusIndicator: React.FC<{
  isPending: boolean;
  isBooked: boolean;
  variant: "compact" | "expanded";
}> = ({ isPending, isBooked, variant }) => {
  const compactClasses =
    "absolute bottom-1 right-1 ~w-3/4 ~h-3/4 rounded-full bg-white flex items-center justify-center";
  const expandedClasses = `~w-4/5 ~h-4/5 rounded-full bg-white flex items-center justify-center ring-1 ${
    isBooked
      ? "ring-green-500/20"
      : isPending
      ? "ring-orange-500/50"
      : "ring-rose-500/20"
  }`;
  return (
    <div className={variant === "compact" ? compactClasses : expandedClasses}>
      {isBooked ? (
        <Check className="~w-2/3 ~h-2/3 text-green-500" />
      ) : isPending ? (
        <Minus className="~w-2/3 ~h-2/3 text-orange-500" />
      ) : (
        <X className="~w-2/3 ~h-2/3 text-rose-500" />
      )}
    </div>
  );
};

const CategoryCard: React.FC<{
  config: CategoryConfig;
  items: BudgetBreakdownItem[];
  variant: "compact" | "expanded";
  onClick?: () => void;
}> = ({ config, items, variant, onClick }) => {
  const isPending = items.some(
    (item) =>
      item.bookingStatus === "pending" || item.bookingStatus === "not_booked"
  );
  const isBooked =
    items.length > 0 && items.every((item) => item.bookingStatus === "booked");

  const isCompact = variant === "compact";
  const baseClasses = cn(
    "transition-all duration-200 relative",
    {
      "flex justify-center items-center ~py-1/2 w-full rounded-lg": isCompact,
      "flex flex-row items-center py-2 ~3xl/4xl:~py-1/3 rounded-lg justify-between":
        !isCompact,
    },
    isBooked ? "bg-indigo-50" : isPending ? "bg-orange-50" : "bg-neutral-100"
  );

  const iconClasses = cn(
    "flex items-center justify-center",
    {
      "~w-8/12 ~h-8/12": isCompact,
      "w-10 h-10": !isCompact,
    },
    isBooked
      ? "text-indigo-600"
      : isPending
      ? "text-orange-500"
      : "text-neutral-500"
  );

  return (
    <div className={baseClasses} onClick={onClick}>
      {!isCompact ? (
        <>
          <div className="flex flex-row items-center justify-between w-full pr-2">
            <div className="flex flex-row items-center space-x-1">
              <div className={iconClasses}>
                <i
                  className={cn("fa-solid", config.icon, {
                    "text-sm 3xl:text-base": isCompact,
                    "text-base 3xl:text-lg": !isCompact,
                  })}
                />
              </div>
              <span className="text-sm 3xl:text-[12px] 3xl:text-[13px] font-medium text-neutral-700">
                {config.label}
              </span>
            </div>
            <StatusIndicator
              isBooked={isBooked}
              isPending={isPending}
              variant={variant}
            />
            {/* <div
            className={cn(
              "inline-flex items-center justify-center px-3 py-1 3xl:px-2 3xl:py-0.5 3xl:px-4 3xl:py-1.5 rounded-full text-sm 3xl:text-[10px] 3xl:text-[12px] font-medium",
              isBooked
                ? "bg-green-500/10 text-green-600 hover:bg-green-500/20"
                : isPending
                ? "bg-yellow-500/10 text-yellow-600 hover:bg-yellow-500/20"
                : "bg-gray-500/10 text-gray-600 hover:bg-gray-500/20"
            )}
          ></div> */}
          </div>
        </>
      ) : (
        <>
          <div className={iconClasses}>
            <i
              className={cn(
                "fa-solid",
                config.icon,
                "text-xs sm:text-base 3xl:text-sm"
              )}
            />
            <StatusIndicator
              isBooked={isBooked}
              isPending={isPending}
              variant={variant}
            />
          </div>
          <span className="text-[10px] sm:text-xs 3xl:text-[10px] mt-1 3xl:mt-0.5 font-medium text-neutral-600 min-h-[600px]:block hidden">
            {items.length === 0
              ? config.label
              : `${
                  items.filter((item) => item.bookingStatus === "booked").length
                }/${items.length} ${config.label}`}
          </span>
        </>
      )}
    </div>
  );
};

export default CategoryCard;
